import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import Navbar from '../components/Navbar';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { ScrollArea } from '../components/ui/scroll-area';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { toast } from 'sonner';
import { FileText, CheckCircle2, Clock, PenLine, ArrowRight } from 'lucide-react';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ConstitutionPage = () => {
  const [constitution, setConstitution] = useState(null);
  const [signatureStatus, setSignatureStatus] = useState(null);
  const [language, setLanguage] = useState('en');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [constRes, statusRes] = await Promise.all([
        axios.get(`${API_URL}/constitution`),
        axios.get(`${API_URL}/signatures/status`)
      ]);
      setConstitution(constRes.data);
      setSignatureStatus(statusRes.data);
    } catch (error) {
      if (error.response?.status !== 404) {
        toast.error('Failed to load constitution');
      }
    } finally {
      setLoading(false);
    }
  };

  const renderContent = (content) => {
    return content?.split('\n').map((line, index) => {
      if (line.startsWith('# ')) {
        return <h1 key={index} className="text-2xl font-serif font-bold text-cameroon-green mt-8 mb-4">{line.slice(2)}</h1>;
      } else if (line.startsWith('## ')) {
        return <h2 key={index} className="text-xl font-serif font-semibold text-cameroon-red mt-6 mb-3">{line.slice(3)}</h2>;
      } else if (line.startsWith('### ')) {
        return <h3 key={index} className="text-lg font-semibold mt-4 mb-2">{line.slice(4)}</h3>;
      } else if (line.startsWith('**') && line.endsWith('**')) {
        return <p key={index} className="font-semibold mt-3 mb-2">{line.slice(2, -2)}</p>;
      } else if (line.startsWith('- ')) {
        return <li key={index} className="ml-6 mb-1 list-disc">{line.slice(2)}</li>;
      } else if (line === '---') {
        return <hr key={index} className="my-6 border-border" />;
      } else if (line.trim()) {
        return <p key={index} className="mb-3 leading-relaxed">{line}</p>;
      }
      return null;
    });
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background">
        <Navbar />
        <div className="flex items-center justify-center h-[60vh]">
          <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <Navbar />
      
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="grid lg:grid-cols-3 gap-8">
          {/* Constitution Content */}
          <div className="lg:col-span-2 space-y-6">
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
              <div>
                <h1 className="font-serif text-3xl font-bold">CCA Constitution</h1>
                <p className="text-muted-foreground mt-1">
                  Version {constitution?.version || '1.0'} • Last updated: {constitution?.updated_at?.slice(0, 10) || 'N/A'}
                </p>
              </div>
              <Tabs value={language} onValueChange={setLanguage}>
                <TabsList>
                  <TabsTrigger value="en">English</TabsTrigger>
                  <TabsTrigger value="fr">Français</TabsTrigger>
                </TabsList>
              </Tabs>
            </div>
            
            <Card>
              <CardContent className="p-6 sm:p-8">
                <ScrollArea className="h-[65vh]">
                  <div className="prose prose-sm max-w-none pr-4">
                    {constitution ? (
                      language === 'en' 
                        ? renderContent(constitution.content)
                        : renderContent(constitution.content_fr)
                    ) : (
                      <div className="text-center py-12">
                        <FileText className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
                        <p className="text-muted-foreground">No constitution available</p>
                      </div>
                    )}
                  </div>
                </ScrollArea>
              </CardContent>
            </Card>
          </div>
          
          {/* Sidebar */}
          <div className="space-y-6">
            {/* Signature Status */}
            <Card>
              <CardHeader>
                <CardTitle className="font-serif text-lg flex items-center gap-2">
                  <FileText className="w-5 h-5 text-cameroon-green" />
                  Signature Progress
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="text-center py-4">
                  <div className="text-4xl font-bold text-cameroon-green">
                    {signatureStatus?.signed || 0}
                  </div>
                  <div className="text-sm text-muted-foreground">
                    of {signatureStatus?.total || 12} associations signed
                  </div>
                </div>
                
                <div className="w-full bg-muted rounded-full h-3">
                  <div 
                    className="bg-cameroon-green h-3 rounded-full transition-all duration-500"
                    style={{ width: `${(signatureStatus?.signed / signatureStatus?.total) * 100 || 0}%` }}
                  />
                </div>
                
                {/* Signed List */}
                <div className="space-y-3">
                  <div className="flex items-center gap-2 text-sm font-medium text-cameroon-green">
                    <CheckCircle2 className="w-4 h-4" />
                    Signed ({signatureStatus?.signed_associations?.length || 0})
                  </div>
                  <div className="flex flex-wrap gap-1">
                    {signatureStatus?.signed_associations?.map((assoc) => (
                      <Badge key={assoc.association_id} className="bg-cameroon-green/10 text-cameroon-green text-xs">
                        {assoc.short_name}
                      </Badge>
                    ))}
                    {signatureStatus?.signed_associations?.length === 0 && (
                      <span className="text-xs text-muted-foreground">None yet</span>
                    )}
                  </div>
                </div>
                
                <div className="space-y-3">
                  <div className="flex items-center gap-2 text-sm font-medium text-cameroon-yellow">
                    <Clock className="w-4 h-4" />
                    Pending ({signatureStatus?.pending_associations?.length || 0})
                  </div>
                  <div className="flex flex-wrap gap-1">
                    {signatureStatus?.pending_associations?.map((assoc) => (
                      <Badge key={assoc.association_id} variant="outline" className="text-xs">
                        {assoc.short_name}
                      </Badge>
                    ))}
                  </div>
                </div>
              </CardContent>
            </Card>
            
            {/* Sign CTA */}
            <Card className="bg-cameroon-green/5 border-cameroon-green/20">
              <CardContent className="p-6 text-center">
                <PenLine className="w-10 h-10 text-cameroon-green mx-auto mb-4" />
                <h3 className="font-serif font-semibold mb-2">Ready to Sign?</h3>
                <p className="text-sm text-muted-foreground mb-4">
                  Association presidents can sign the constitution electronically.
                </p>
                <Link to="/sign">
                  <Button className="w-full bg-cameroon-green hover:bg-cameroon-green/90" data-testid="sign-constitution-btn">
                    Sign Now
                    <ArrowRight className="w-4 h-4 ml-2" />
                  </Button>
                </Link>
              </CardContent>
            </Card>
          </div>
        </div>
      </main>
    </div>
  );
};

export default ConstitutionPage;
